import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

export const PROFILE_KEY = "mt_active_profile";

// tiny per-page-load response cache so revisiting a tab paints instantly, then refreshes
const memo = new Map();
const ck = (path, params) => `${path}|${JSON.stringify(params || {})}`;

export const cachedGet = (path, params) => memo.get(ck(path, params));

export const getFresh = async (path, params, keyParams = params) => {
  const { data } = await api.get(path, { params });
  memo.set(ck(path, keyParams), data);
  return data;
};

api.interceptors.response.use((r) => {
  if ((r.config.method || "get").toLowerCase() !== "get") memo.clear();
  return r;
});

api.interceptors.request.use((config) => {
  const p = localStorage.getItem(PROFILE_KEY);
  if (p) config.params = { ...(config.params || {}), profile: p };
  return config;
});

export const shiftDate = (dateStr, delta) => {
  const d = new Date(dateStr + "T12:00:00");
  d.setDate(d.getDate() + delta);
  return todayStr(d);
};

export const todayStr = (d = new Date()) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// locale-independent "10 Aug"
export const fmtDayMonth = (dateStr) => {
  const d = new Date(dateStr + "T12:00:00");
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
};

// locale-independent "Mon, 10 Aug"
export const fmtDayLabel = (dateStr) => {
  const d = new Date(dateStr + "T12:00:00");
  return `${WEEKDAYS[d.getDay()]}, ${fmtDayMonth(dateStr)}`;
};

// locale-independent "09-Aug-2026"
export const fmtDMY = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T12:00:00");
  return `${String(d.getDate()).padStart(2, "0")}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
};

export const nowHM = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
};

export const fmt12 = (hm) => {
  if (!hm) return "";
  const [h, m] = hm.split(":").map(Number);
  const ap = h >= 12 ? "pm" : "am";
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
};

export const minutesSince = (hm) => {
  const [h, m] = hm.split(":").map(Number);
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes() - (h * 60 + m);
};

export const overdueLabel = (hm) => {
  const mins = Math.max(minutesSince(hm), 0);
  if (mins < 60) return `Overdue ${mins}m`;
  return `Overdue ${(mins / 60).toFixed(1)}h`;
};

// compact inline variant: "45m" / "3.5h"
export const lateLabel = (hm) => {
  const mins = Math.max(minutesSince(hm), 0);
  return mins < 60 ? `${mins}m` : `${(mins / 60).toFixed(1)}h`;
};

// every 30-minute slot of today in clock order; past ones flagged so they can be greyed out
export const allDaySlots = () => {
  const cut = nextSlot();
  const slots = [];
  for (let t = 0; t <= 23 * 60 + 30; t += 30) {
    const hm = `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
    slots.push({ value: hm, label: fmt12(hm), past: hm < cut });
  }
  return slots;
};

// local wall-clock time of a stored UTC ISO timestamp, e.g. "09:34 am"
export const fmtActedAt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const h = d.getHours();
  const ap = h >= 12 ? "pm" : "am";
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${String(hh).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")} ${ap}`;
};

// "09:34 am" for the time a dose was actioned (stored local HH:MM wins over the UTC timestamp)
export const fmtActed = (item) => {
  const hm = actedHM(item);
  if (!hm) return "";
  const [h, m] = hm.split(":").map(Number);
  const ap = h >= 12 ? "pm" : "am";
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${String(hh).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ap}`;
};

// HH:MM the dose was actioned at — the stored local value wins over the UTC timestamp
export const actedHM = (item) => {
  if (item?.acted_hm) return item.acted_hm;
  if (!item?.acted_at) return "";
  const d = new Date(item.acted_at);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

export const nextSlot = () => {
  const d = new Date();
  const t = Math.min(Math.floor((d.getHours() * 60 + d.getMinutes()) / 30) * 30 + 30, 23 * 60 + 30);
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};

export const effectiveStatus = (item, date) => {
  const today = todayStr();
  if (item.status === "taken" || item.status === "skipped") return item.status;
  const isPast = date < today;
  if (item.status === "snoozed") {
    if (isPast) return "missed";
    if (date === today && item.snooze_until && nowHM() >= item.snooze_until) return "snooze-due";
    return "snoozed";
  }
  if (isPast) return "missed";
  if (date === today && nowHM() >= item.time) return "overdue";
  return "pending";
};
